import {useCallback, useReducer } from "react";

type AlarmLynxViewStateType = {
    // Options state
    filterText: string;

    // Other state
    isNoData: boolean;
    isOptionsDirty: boolean;
    downloadUid: string | undefined;
    refreshing: boolean;

    summaryListingAlarmAtTop: boolean;
    initialSummaryListingAlarmAtTop?: boolean;
};

const initialState: AlarmLynxViewStateType = {
    filterText: "",
    isNoData: false,
    isOptionsDirty: false,
    downloadUid: undefined,
    refreshing: false,
    summaryListingAlarmAtTop: false,
};

enum AlarmLynxViewActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    NoDataChanged = "NO_DATA_CHANGED",
    RefreshingChanged = "REFRESHING_CHANGED",
    DownloadUidChanged = 'ALARMLYNX_DOWNLOAD_UID_CHANGED',
    DisplayAlarmsAtTopChanged = "ALARMLYNX_ALARMS_AT_TOP",
}

type AlarmLynxViewActionTypes = {
    type: AlarmLynxViewActionEnum.ResetOptionsRequested;
} | {
    type: AlarmLynxViewActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: AlarmLynxViewActionEnum.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: AlarmLynxViewActionEnum.RefreshingChanged;
    payload: boolean;
} | {
    type: AlarmLynxViewActionEnum.DisplayAlarmsAtTopChanged;
    payload: boolean;
} | {
    type: AlarmLynxViewActionEnum.NoDataChanged;
    isNoData: boolean;
};


function reducer(state: AlarmLynxViewStateType, action: AlarmLynxViewActionTypes): AlarmLynxViewStateType {

    const copy = { ...state };
    switch (action.type) {

        case AlarmLynxViewActionEnum.ResetOptionsRequested:
            copy.filterText = initialState.filterText;
            copy.isOptionsDirty = false;
            return copy;

        case AlarmLynxViewActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case AlarmLynxViewActionEnum.NoDataChanged:
            copy.isNoData = action.isNoData;
            return copy;

        case AlarmLynxViewActionEnum.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case AlarmLynxViewActionEnum.RefreshingChanged:
            copy.refreshing = action.payload;
            return copy;

        case AlarmLynxViewActionEnum.DisplayAlarmsAtTopChanged:
            copy.summaryListingAlarmAtTop = action.payload;
            if (copy.initialSummaryListingAlarmAtTop === undefined) {
                copy.initialSummaryListingAlarmAtTop = action.payload;
            }
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;


        default:
            return state;
    }
}

function isOptionsDirty(state: AlarmLynxViewStateType) {
    return state.filterText !== initialState.filterText ||
    (state.initialSummaryListingAlarmAtTop !== undefined && state.initialSummaryListingAlarmAtTop !== state.summaryListingAlarmAtTop);
}

type UseAlarmLynxViewStateType = {
    state: AlarmLynxViewStateType;
    resetOptions: () => void;
    setFilterText: (filterText: string) => void;
    setNoData: (isNoData: boolean) => void;
    setDownloadUid: (value: string | undefined) => void;
    setRefreshing: (value: boolean) => void;
    setAlarmsAtTop: (value: boolean) => void;
};

export default function useAlarmLynxViewState(): UseAlarmLynxViewStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback(() => dispatch({
            type: AlarmLynxViewActionEnum.ResetOptionsRequested
        }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
            type: AlarmLynxViewActionEnum.FilterTextChanged,
            filterText: filterText
        }), []);

    const setNoData = useCallback((isNoData: boolean) => dispatch({
            type: AlarmLynxViewActionEnum.NoDataChanged,
            isNoData: isNoData
        }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: AlarmLynxViewActionEnum.DownloadUidChanged,
        payload: value
    }), []);

    const setRefreshing = useCallback((value: boolean) => dispatch({
        type: AlarmLynxViewActionEnum.RefreshingChanged,
        payload: value
    }), []);

    const setAlarmsAtTop = useCallback((value: boolean) => dispatch({
        type: AlarmLynxViewActionEnum.DisplayAlarmsAtTopChanged,
        payload: value
    }), []);
    
    


    return {
        state,
        resetOptions,
        setFilterText,
        setNoData,
        setDownloadUid,
        setRefreshing,
        setAlarmsAtTop
    };
}    
